import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FolderOpen as FolderIcon,
  Settings as SettingsIcon,
  LogOut as LogOutIcon,
  Moon as MoonIcon,
  Sun as SunIcon,
  Globe as GlobeIcon,
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { useTheme } from "../../hooks/useTheme";
import { signOut } from "../../lib/api/auth";
import { setLanguage, type SupportedLang } from "../../i18n";

/**
 * Compact user menu in the editor header.
 * Click the avatar (first letter of email) to open a dropdown with
 * current email, a "My projects" link, "Settings", and Sign out.
 */
export function UserMenu() {
  const { t, i18n } = useTranslation();
  const { session, loading } = useAuth();
  const [theme, , toggleTheme] = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const currentLang = (i18n.language?.slice(0, 2) ?? "en") as SupportedLang;

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (loading) return null;
  if (!session) return null;

  const email = session.user.email;
  const initial = email.charAt(0).toUpperCase();

  const handleSignOut = async () => {
    setOpen(false);
    await signOut();
    navigate("/", { replace: true });
  };

  const handleToggleLang = async () => {
    const next: SupportedLang = currentLang === "ar" ? "en" : "ar";
    await setLanguage(next);
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("nav.userMenu")}
        className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-700 text-sm font-semibold text-white hover:bg-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
      >
        {initial}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute end-0 top-11 z-50 w-56 overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800"
        >
          <div className="border-b border-slate-200 px-4 py-3 text-xs dark:border-slate-700">
            <p className="text-slate-500">{t("nav.signedInAs")}</p>
            <p className="mt-0.5 truncate font-medium text-slate-900 dark:text-slate-100">
              {email}
            </p>
          </div>

          <Link
            to="/projects"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-slate-100 dark:hover:bg-slate-700"
          >
            <FolderIcon className="h-4 w-4 text-slate-500" />
            {t("nav.myProjects")}
          </Link>
          <Link
            to="/settings"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-slate-100 dark:hover:bg-slate-700"
          >
            <SettingsIcon className="h-4 w-4 text-slate-500" />
            {t("nav.settings")}
          </Link>
          <button
            type="button"
            role="menuitem"
            onClick={toggleTheme}
            className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-700"
          >
            {theme === "dark" ? (
              <SunIcon className="h-4 w-4 text-slate-500" />
            ) : (
              <MoonIcon className="h-4 w-4 text-slate-500" />
            )}
            {theme === "dark" ? "Light mode" : "Dark mode"}
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={handleToggleLang}
            className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-700"
          >
            <GlobeIcon className="h-4 w-4 text-slate-500" />
            {currentLang === "ar" ? "English" : "العربية"}
          </button>

          <div className="border-t border-slate-200 dark:border-slate-700">
            <button
              type="button"
              role="menuitem"
              onClick={handleSignOut}
              className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
            >
              <LogOutIcon className="h-4 w-4" />
              {t("nav.signOut")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
